import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatIDR } from "@/lib/format";
import { Field, EmptyState } from "@/components/ui-helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ScanLine, Loader2, Check, AlertTriangle, ChevronDown, Package, Layers, Plus, X } from "lucide-react";
import { toast } from "sonner";

const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

// Shared: turn a scan-preview order into editable resolver state
function toResolved(preview) {
  const c = preview.customer || {};
  return {
    _key: uid(),
    trip_id: preview.trip?.id,
    reference: preview.reference || "",
    notes: preview.notes || "",
    ready: preview.ready,
    issues: preview.issues || [],
    method: preview.method,
    customer: {
      mode: c.status === "matched" ? "existing" : (c.status === "ambiguous" ? "existing" : "new"),
      customer_id: c.customer_id || (c.candidates?.[0]?.id || ""),
      name: c.name || "",
      whatsapp: c.whatsapp || "",
      status: c.status,
      candidates: c.candidates || [],
    },
    items: (preview.items || []).map((it) => ({
      _key: uid(),
      raw_name: it.raw_name,
      quantity: it.quantity || 1,
      pasted_unit_price: it.pasted_unit_price,
      price_mismatch: it.price_mismatch,
      system_price: it.match?.system_price,
      product_id: it.match?.product_id || "",
      status: it.match?.status,
      candidates: it.match?.candidates || [],
      use_pasted_price: !!it.price_mismatch, // pasted wins by default, flagged
      unit_price: it.pasted_unit_price || "",
    })),
  };
}

function commitPayload(r) {
  return {
    trip_id: r.trip_id,
    reference: r.reference,
    notes: r.notes,
    ready: isReady(r),
    idempotency_key: r._idem || (r._idem = uid()),
    customer: r.customer.mode === "existing"
      ? { customer_id: r.customer.customer_id }
      : { name: r.customer.name, whatsapp: r.customer.whatsapp },
    items: r.items.map((it) => ({
      product_id: it.product_id,
      quantity: Number(it.quantity) || 1,
      unit_price: it.use_pasted_price ? Number(it.unit_price) || 0 : null,
      use_pasted_price: !!it.use_pasted_price,
    })),
  };
}

function isReady(r) {
  const custOk = r.customer.mode === "existing" ? !!r.customer.customer_id : !!(r.customer.name || "").trim();
  const itemsOk = r.items.length > 0 && r.items.every((it) => !!it.product_id);
  return custOk && itemsOk;
}

// Shared editor for one resolved order (customer + items)
function ResolverBlock({ r, setR, tripProducts, customers }) {
  const upd = (patch) => setR({ ...r, ...patch });
  const updCust = (patch) => setR({ ...r, customer: { ...r.customer, ...patch } });
  const updItem = (key, patch) => setR({ ...r, items: r.items.map((it) => it._key === key ? { ...it, ...patch } : it) });
  const addItem = () => setR({ ...r, items: [...r.items, { _key: uid(), raw_name: "Produk ditambah manual", quantity: 1, product_id: "", status: "manual", candidates: [], use_pasted_price: false, unit_price: "" }] });
  const rmItem = (key) => setR({ ...r, items: r.items.filter((it) => it._key !== key) });
  return (
    <div className="space-y-3">
      {/* Customer */}
      <div className="rounded-lg border border-border p-3 space-y-2" data-testid="scan-customer-block">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium">Pelanggan</p>
          <div className="flex gap-1">
            <Button type="button" size="sm" variant={r.customer.mode === "existing" ? "default" : "outline"} className="h-7" onClick={() => updCust({ mode: "existing" })} data-testid="scan-cust-existing">Pilih ada</Button>
            <Button type="button" size="sm" variant={r.customer.mode === "new" ? "default" : "outline"} className="h-7" onClick={() => updCust({ mode: "new" })} data-testid="scan-cust-new">Baru</Button>
          </div>
        </div>
        {r.customer.status === "ambiguous" && r.customer.mode === "existing" && (
          <p className="text-[11px] text-[hsl(var(--warning))] flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Beberapa pelanggan cocok — pilih satu.</p>
        )}
        {r.customer.mode === "existing" ? (
          <SearchableSelect
            value={r.customer.customer_id}
            onChange={(v) => updCust({ customer_id: v })}
            options={customers.map((c) => ({
              value: c.id,
              label: c.name || "(tanpa nama)",
              sublabel: [c.whatsapp, c.instagram].filter(Boolean).join(" · "),
              keywords: [c.name, c.whatsapp, c.phone, c.instagram, c.email],
            }))}
            placeholder="Pilih pelanggan"
            searchPlaceholder="Cari nama / WhatsApp / IG..."
            emptyText="Pelanggan tidak ditemukan"
            testId="scan-customer-select"
          />
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Nama" value={r.customer.name} onChange={(e) => updCust({ name: e.target.value })} data-testid="scan-new-name" />
            <Input placeholder="WhatsApp" value={r.customer.whatsapp} onChange={(e) => updCust({ whatsapp: e.target.value })} data-testid="scan-new-wa" />
          </div>
        )}
        {r.customer.mode === "new" && r.customer.status !== "matched" && <Badge variant="secondary" className="text-[10px]">Customer baru</Badge>}
      </div>

      {/* Items */}
      <div className="space-y-2">
        {r.items.map((it) => {
          const needs = !it.product_id;
          return (
            <div key={it._key} className={`rounded-lg border p-3 space-y-2 ${needs ? "border-[hsl(var(--warning))]/50 bg-[hsl(var(--warning))]/5" : "border-border"}`} data-testid="scan-item-row">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm truncate">"{it.raw_name}"</p>
                <div className="flex items-center gap-1.5 shrink-0">
                  {needs ? <Badge className="bg-[hsl(var(--warning))] text-white text-[10px]" data-testid="scan-item-needs">Perlu dikonfirmasi</Badge>
                    : <Badge variant="secondary" className="text-[10px]"><Check className="h-3 w-3 mr-0.5" />Cocok</Badge>}
                  <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => rmItem(it._key)} data-testid="scan-item-remove"><X className="h-4 w-4" /></Button>
                </div>
              </div>
              <Field label="Produk">
                <SearchableSelect
                  showImages
                  value={it.product_id}
                  onChange={(v) => { const p = tripProducts.find((x) => x.id === v); updItem(it._key, { product_id: v, system_price: p?.pricing?.final_price }); }}
                  options={tripProducts.map((p) => ({
                    value: p.id,
                    label: p.name,
                    sublabel: [p.product_code, p.brand, p.category, p.variant].filter(Boolean).join(" · "),
                    image: p.thumb_url || p.photo || null,
                    keywords: [p.name, p.brand, p.category, p.variant, p.size, p.colour, p.sku, p.product_code],
                  }))}
                  placeholder="Pilih produk"
                  searchPlaceholder="Cari nama / brand / kategori / SKU..."
                  emptyText="Produk tidak ditemukan"
                  testId="scan-product-select"
                />
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Qty"><Input type="number" min="1" value={it.quantity} onChange={(e) => updItem(it._key, { quantity: e.target.value })} data-testid="scan-item-qty" /></Field>
                <Field label="Harga satuan">
                  <Input type="number" value={it.use_pasted_price ? it.unit_price : (it.system_price ?? "")} disabled={!it.use_pasted_price}
                    onChange={(e) => updItem(it._key, { unit_price: e.target.value })} data-testid="scan-item-price" />
                </Field>
              </div>
              {it.price_mismatch && (
                <label className="flex items-center gap-2 text-[11px] text-muted-foreground cursor-pointer">
                  <input type="checkbox" checked={it.use_pasted_price} onChange={(e) => updItem(it._key, { use_pasted_price: e.target.checked })} data-testid="scan-use-pasted" />
                  Pakai harga dari teks ({formatIDR(it.pasted_unit_price)}) — sistem: {formatIDR(it.system_price)}
                </label>
              )}
            </div>
          );
        })}
        <Button type="button" variant="outline" size="sm" className="w-full border-dashed" onClick={addItem} data-testid="scan-add-item">
          <Plus className="h-3.5 w-3.5 mr-1.5" />Tambah Produk
        </Button>
      </div>
      <Field label="Catatan"><Textarea value={r.notes} onChange={(e) => upd({ notes: e.target.value })} data-testid="scan-notes" /></Field>
    </div>
  );
}

// ---- Single scan tab ----
export function ScanSingle({ trips, trip_id, setTripId, tripProducts, customers, onCreated }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [r, setR] = useState(null);
  const [dup, setDup] = useState(null);

  const doScan = async () => {
    if (!trip_id) return toast.error("Pilih trip");
    if (!text.trim()) return toast.error("Tempel teks order");
    setBusy(true);
    try {
      const res = await api.post("/orders/scan", { text, trip_id });
      setR(toResolved(res.data));
      if (res.data.issues?.length) toast.warning(`${res.data.issues.length} hal perlu dicek`);
      else toast.success("Berhasil dibaca — periksa hasilnya");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal memindai"); }
    finally { setBusy(false); }
  };

  const create = async (force = false) => {
    if (!isReady(r)) return toast.error("Lengkapi produk & pelanggan dulu");
    setBusy(true);
    try {
      const payload = { ...commitPayload(r), force };
      const res = await api.post("/orders/scan/commit", payload);
      toast.success(`Order dibuat · ${res.data.order.order_number}`);
      setR(null); setText(""); setDup(null); onCreated?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "DUPLICATE") setDup(d);
      else toast.error((typeof d === "string" ? d : null) || "Gagal membuat order");
    } finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="scan-single">
      <div className="space-y-3">
        <Field label="Trip" required>
          <SearchableSelect
            value={trip_id}
            onChange={setTripId}
            options={trips.map((t) => ({ value: t.id, label: t.name, keywords: [t.name, t.code] }))}
            placeholder="Pilih trip"
            searchPlaceholder="Cari trip..."
            emptyText="Trip tidak ditemukan"
            testId="scan-trip-select"
          />
        </Field>
        <Field label="Tempel teks order dari WhatsApp">
          <Textarea rows={16} className="font-mono text-xs" placeholder={"Tempel konfirmasi / pesan order di sini…\n\nContoh:\nPelanggan: Jessica\nWhatsApp: 08123456789\n1. Weet-Bix 375g\n   Qty: 2\n   Harga: Rp120.000"} value={text} onChange={(e) => setText(e.target.value)} data-testid="scan-textarea" />
        </Field>
        <Button onClick={doScan} disabled={busy} className="w-full" data-testid="scan-button">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ScanLine className="h-4 w-4 mr-1.5" />}Scan Order</Button>
      </div>
      <div className="space-y-3">
        {!r ? <EmptyState icon={ScanLine} title="Hasil scan tampil di sini" subtitle="Tempel teks lalu tekan Scan Order untuk melihat pratinjau." /> : (
          <>
            <div className="flex items-center justify-between">
              <p className="font-medium text-sm">Hasil Scan {r.method === "ai" && <Badge variant="secondary" className="ml-1 text-[10px]">AI</Badge>}</p>
              {isReady(r) ? <Badge variant="secondary" className="text-[10px]"><Check className="h-3 w-3 mr-0.5" />Siap</Badge> : <Badge className="bg-[hsl(var(--warning))] text-white text-[10px]">Perlu dilengkapi</Badge>}
            </div>
            <ResolverBlock r={r} setR={setR} tripProducts={tripProducts} customers={customers} />
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => { setR(null); }} data-testid="scan-cancel">Batal</Button>
              <Button className="flex-1" onClick={() => create(false)} disabled={busy || !isReady(r)} data-testid="scan-create">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Check className="h-4 w-4 mr-1.5" />}Buat Order</Button>
            </div>
          </>
        )}
      </div>

      <Dialog open={!!dup} onOpenChange={(o) => !o && setDup(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-[hsl(var(--warning))]" />Kemungkinan duplikat</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Sepertinya order ini sudah ada: <span className="font-medium">{dup?.order_number}</span> ({dup?.reason}). Tetap buat order baru?</p>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setDup(null)}>Batal</Button>
            <Button onClick={() => create(true)} disabled={busy} data-testid="scan-force-create">Tetap Buat</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---- Bulk scan tab ----
export function ScanBulk({ trips, trip_id, setTripId, tripProducts, customers, onCreated }) {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState(null);
  const [summary, setSummary] = useState(null);

  const doScan = async () => {
    if (!trip_id) return toast.error("Pilih trip");
    if (!text.trim()) return toast.error("Tempel teks order");
    setBusy(true); setSummary(null);
    try {
      const res = await api.post("/orders/bulk-scan", { text, trip_id });
      setList(res.data.orders.map(toResolved));
      toast.success(`${res.data.count} order terdeteksi · ${res.data.ready_count} siap`);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal memindai"); }
    finally { setBusy(false); }
  };

  const setOne = (key, next) => setList((l) => l.map((r) => r._key === key ? next : r));

  const createValid = async () => {
    const valid = list.filter((r) => isReady(r));
    if (valid.length === 0) return toast.error("Tidak ada order yang siap dibuat");
    setBusy(true);
    try {
      const res = await api.post("/orders/bulk-scan/commit", { orders: valid.map((r) => ({ ...commitPayload(r), reference: r.reference })) });
      setSummary(res.data);
      toast.success(`${res.data.created_count} order dibuat`);
      onCreated?.();
      // drop created ones from the list
      setList((l) => l.filter((r) => !isReady(r)));
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal membuat order"); }
    finally { setBusy(false); }
  };

  const readyCount = (list || []).filter(isReady).length;

  return (
    <div className="space-y-4" data-testid="scan-bulk">
      {!list ? (
        <div className="max-w-3xl space-y-3">
          <Field label="Trip" required>
            <SearchableSelect
              value={trip_id}
              onChange={setTripId}
              options={trips.map((t) => ({ value: t.id, label: t.name, keywords: [t.name, t.code] }))}
              placeholder="Pilih trip"
              searchPlaceholder="Cari trip..."
              emptyText="Trip tidak ditemukan"
              testId="bulk-trip-select"
            />
          </Field>
          <Field label="Tempel beberapa order sekaligus" hint="Pisahkan tiap order dengan baris ORDER 1 … END ORDER">
            <Textarea rows={16} className="font-mono text-xs" value={text} onChange={(e) => setText(e.target.value)} data-testid="bulk-textarea"
              placeholder={"ORDER 1\nPelanggan: Jessica\nWhatsApp: 0812xxxx\n1. Weet-Bix 375g\n   Qty: 2\nEND ORDER\n\nORDER 2\nPelanggan: Rani\n1. Tim Tam Original\n   Qty: 3\nEND ORDER"} />
          </Field>
          <Button onClick={doScan} disabled={busy} data-testid="bulk-scan-button">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Layers className="h-4 w-4 mr-1.5" />}Scan Semua</Button>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm">
              <span className="font-medium">{list.length}</span> order terdeteksi ·{" "}
              <span className="text-[hsl(var(--success,142_70%_40%))]">{readyCount} siap</span> ·{" "}
              <span className="text-[hsl(var(--warning))]">{list.length - readyCount} perlu dicek</span>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setList(null); setSummary(null); }} data-testid="bulk-reset">Ulang</Button>
              <Button onClick={createValid} disabled={busy || readyCount === 0} data-testid="bulk-create-valid">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Check className="h-4 w-4 mr-1.5" />}Buat {readyCount} Order Valid</Button>
            </div>
          </div>

          {summary && (
            <div className="rounded-xl border border-border bg-card p-4 text-sm space-y-1" data-testid="bulk-summary">
              <p className="font-medium">Ringkasan Import</p>
              <p>Dibuat: <span className="font-semibold text-primary">{summary.created_count}</span></p>
              <p>Dilewati (duplikat): {summary.skipped_count}</p>
              <p>Perlu ditinjau: {summary.needs_review_count}</p>
              {summary.created_count > 0 && <Button variant="link" className="px-0 h-auto" onClick={() => navigate("/orders")}>Lihat di Daftar Order →</Button>}
            </div>
          )}

          <div className="space-y-2">
            {list.map((r, idx) => (
              <Collapsible key={r._key} className="rounded-xl border border-border bg-card" data-testid="bulk-order-card">
                <CollapsibleTrigger className="w-full flex items-center gap-3 p-3 text-left">
                  <span className="text-xs font-mono text-muted-foreground">#{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{r.customer.mode === "existing" ? (customers.find((c) => c.id === r.customer.customer_id)?.name || "(pilih pelanggan)") : (r.customer.name || "(pelanggan baru)")}</p>
                    <p className="text-[11px] text-muted-foreground">{r.items.length} produk{r.items.some((i) => !i.product_id) ? " · ada yang perlu dicek" : ""}</p>
                  </div>
                  {isReady(r) ? <Badge variant="secondary" className="text-[10px]"><Check className="h-3 w-3 mr-0.5" />Siap</Badge> : <Badge className="bg-[hsl(var(--warning))] text-white text-[10px]">Perlu dicek</Badge>}
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </CollapsibleTrigger>
                <CollapsibleContent className="px-3 pb-3 border-t border-border pt-3">
                  <ResolverBlock r={r} setR={(nr) => setOne(r._key, nr)} tripProducts={tripProducts} customers={customers} />
                </CollapsibleContent>
              </Collapsible>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
